import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { makeStyles } from "@mui/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import swal from "sweetalert";
import MsgDataService from "../../services/MsgService";
import CounselorMsgService from "../../services/Counselor/CounselorMsgService";
import {
  getApprovalSendPayload,
  isCounselorScheduleMessage,
  getApprovalMessageTypeLabel,
  formatApprovalScheduleDate } from
"../../utils/approvalSendPayload";
import {
  getRequestStatusKey,
  getRequestStatusLabel,
  isSentFlag } from
"../../utils/approvalStatus";
import {
  APPROVAL_PAGE_SIZE,
  parseApprovalListResponse } from
"../../utils/approvalListResponse";
import ApprovedGroupMessagesFilterDrawer, {
  EMPTY_GROUP_APPROVAL_FILTERS,
  hasActiveGroupApprovalFilters } from
"./ApprovedGroupMessagesFilterDrawer";
import MessagesApprovalSendConfirmModal from "../admin/MessagesApproval/MessagesApprovalSendConfirmModal";
import "../admin/MessagesApproval/MessagesApproval.css";
import "./ApprovedGroupMessagesPanel.css";

const useStyles = makeStyles({
  root: {
    "& .super-app-theme--header": {
      fontSize: "13px"
    }
  }
});

const SEARCH_DEBOUNCE_MS = 500;

const formatRequestDate = (value) => {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MMM DD YYYY, hh:mm A") : value;
};

const normalizeStatus = (status) => String(status || "").toLowerCase();

const getApiErrorMessage = (error) => {
  const data = error?.response?.data;
  if (!data) return error?.message || "Something went wrong. Please try again.";
  if (typeof data === "string") return data;
  if (Array.isArray(data)) return data.join(", ");
  return (
    data.message ||
    data.error_message ||
    data.error ||
    data.detail ||
    "Something went wrong. Please try again.");

};

const showApiError = (error) => {
  swal({
    text: getApiErrorMessage(error),
    icon: "warning",
    dangerMode: true,
    buttons: { confirm: true },
    closeOnClickOutside: false
  });
};

const showSuccess = (message) => {
  swal({
    title: "Success!",
    text: message,
    icon: "success",
    buttons: { OK: true },
    closeOnClickOutside: false
  });
};

const ApprovedGroupMessagesPanel = ({ role = "counselor", onSummaryUpdate }) => {
  const classes = useStyles();
  const [searchText] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionRowId, setActionRowId] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [gridPageSize, setGridPageSize] = useState(APPROVAL_PAGE_SIZE);
  const gridPageSizeRef = useRef(APPROVAL_PAGE_SIZE);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({
    ...EMPTY_GROUP_APPROVAL_FILTERS
  });
  const [draftFilters, setDraftFilters] = useState({
    ...EMPTY_GROUP_APPROVAL_FILTERS
  });
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [sendConfirmRow, setSendConfirmRow] = useState(null);
  const [sendConfirmLoading, setSendConfirmLoading] = useState(false);

  const panelTitle = "Message Requests & Action Items";
  const filtersActive = hasActiveGroupApprovalFilters(appliedFilters);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQ(searchText.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchApprovals = useCallback(() => {
    setLoading(true);
    setRows([]);
    MsgDataService.getMessageApprovals(page, debouncedQ, appliedFilters)
    .then((res) => {
      const { data, payload, items, rowCount, serverPageSize } =
      parseApprovalListResponse(res, {
        knownPageSize: gridPageSizeRef.current,
        currentPage: page
      });
      if (serverPageSize > 0 && serverPageSize !== gridPageSizeRef.current) {
        gridPageSizeRef.current = serverPageSize;
        setGridPageSize(serverPageSize);
      }
      const mappedRows = items.map((item) => ({
        id: item.id,
        requestDate: formatRequestDate(item.request_initiate_time),
        subjectTarget: item.message_body || "",
        requester: item.requester?.name || item.approver_name || "",
        messageType: getApprovalMessageTypeLabel(item),
        scheduleDate: formatApprovalScheduleDate(item),
        isSchedule: isCounselorScheduleMessage(item),
        requestStatus: item.request_status,
        statusKey: getRequestStatusKey(item),
        isSent: isSentFlag(item.is_sent),
        raw: item
      }));
      setRows(mappedRows);

      const summaryTotal = payload.total ?? data.count ?? rowCount;
      const approvedCount = payload.approved ?? 0;
      const pendingCount = payload.pending ?? 0;

      setTotalCount(rowCount);

      if (onSummaryUpdate) {
        onSummaryUpdate({
          totalMessages: summaryTotal,
          approvedGroups: approvedCount,
          approvedMessages: approvedCount,
          pendingMessages: pendingCount,
          newStudentMessages: pendingCount,
          deletedStudentMessages: 0,
          approvedGroupMessages: approvedCount
        });
      }
    })
    .catch(() => {
      setRows([]);
      setTotalCount(0);
    })
    .finally(() => setLoading(false));
  }, [page, debouncedQ, appliedFilters, onSummaryUpdate]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const handlePageChange = (newPage) => {
    setPage(newPage + 1);
  };

  const openFilterDrawer = () => {
    setDraftFilters({ ...appliedFilters });
    setFilterDrawerOpen(true);
  };

  const closeFilterDrawer = () => {
    setFilterDrawerOpen(false);
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...draftFilters });
    setPage(1);
    setFilterDrawerOpen(false);
  };

  const handleResetFilters = () => {
    const empty = { ...EMPTY_GROUP_APPROVAL_FILTERS };
    setDraftFilters(empty);
    if (!filtersActive) {
      setFilterDrawerOpen(false);
      return;
    }
    setAppliedFilters(empty);
    setPage(1);
    setFilterDrawerOpen(false);
  };

  const closeSendConfirm = useCallback(() => {
    if (sendConfirmLoading) return;
    setSendConfirmOpen(false);
    setSendConfirmRow(null);
  }, [sendConfirmLoading]);

  const handleSendClick = useCallback(
    (row) => {
      if (row.requestStatus !== "approved" || row.isSent || row.statusKey === "sent") return;
      const item = row.raw || {};
      const payload = getApprovalSendPayload(item);
      if (!payload) {
        swal({
          text: "Send payload is missing for this approved message.",
          icon: "warning",
          dangerMode: true,
          buttons: { confirm: true },
          closeOnClickOutside: false
        });
        return;
      }

      setSendConfirmRow(row);
      setSendConfirmOpen(true);
    },
    []
  );

  const handleSendConfirm = useCallback(async () => {
    const row = sendConfirmRow;
    if (!row || sendConfirmLoading) return;

    const item = row.raw || {};
    const payload = getApprovalSendPayload(item);
    if (!payload) {
      closeSendConfirm();
      swal({
        text: "Send payload is missing for this approved message.",
        icon: "warning",
        dangerMode: true,
        buttons: { confirm: true },
        closeOnClickOutside: false
      });
      return;
    }

    setSendConfirmLoading(true);
    setActionRowId(row.id);
    try {
      if (role === "recruiter") {
        const response = await MsgDataService.sendMessage(payload);
        await MsgDataService.updateApprovalSendStatus(row.id);
        setSendConfirmOpen(false);
        setSendConfirmRow(null);
        showSuccess(
          response?.data?.message ||
          response?.data?.Status ||
          "Your message has been sent successfully!"
        );
      } else {
        const response = isCounselorScheduleMessage(item) ?
        await CounselorMsgService.sendScheduleMessage(payload) :
        await CounselorMsgService.sendGroupMessage(payload);
        await MsgDataService.updateApprovalSendStatus(row.id);
        setSendConfirmOpen(false);
        setSendConfirmRow(null);
        showSuccess(
          response?.data?.message ||
          response?.data?.Status || (
          isCounselorScheduleMessage(item) ?
          "Your schedule message has been sent successfully!" :
          "Your message has been sent successfully!")
        );
      }
      fetchApprovals();
    } catch (error) {
      showApiError(error);
    } finally {
      setSendConfirmLoading(false);
      setActionRowId(null);
    }
  }, [sendConfirmRow, sendConfirmLoading, role, fetchApprovals, closeSendConfirm]);

  const handleDeleteClick = useCallback(
    async (row) => {
      const confirmed = await swal({
        title: "Delete Message?",
        text: "Are you sure you want to delete this message request?",
        icon: "warning",
        buttons: {
          cancel: "Cancel",
          confirm: "Yes"
        },
        dangerMode: true,
        closeOnClickOutside: false
      });
      if (!confirmed) return;

      setActionRowId(row.id);
      try {
        const response = await MsgDataService.deleteMessageApproval(row.id);
        showSuccess(
          response?.data?.message || "Message request deleted successfully."
        );
        fetchApprovals();
      } catch (error) {
        showApiError(error);
      } finally {
        setActionRowId(null);
      }
    },
    [fetchApprovals]
  );

  const columns = useMemo(
    () => [
    {
      field: "requestDate",
      headerName: "Request Date",
      flex: 1.2,
      minWidth: 190,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) =>
      <span className="approved-group-cell-date">{params.value}</span>

    },
    {
      field: "subjectTarget",
      headerName: "Message Body",
      flex: 1.4,
      minWidth: 200,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) =>
      <span className="approved-group-cell-subject" title={params.value}>
            {params.value}
          </span>

    },
    {
      field: "messageType",
      headerName: "Message Type",
      flex: 1,
      minWidth: 160,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) =>
      <span
        className={`approved-group-message-type ${
        params.row.isSchedule ? "schedule" : "regular"
        }`}
        title={params.value}>

            {params.value}
          </span>

    },
    {
      field: "scheduleDate",
      headerName: " Schedule Date",
      flex: 1.1,
      minWidth: 170,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) =>
      <span
        className="approved-group-cell-date"
        title={params.value || ""}>

            {params.value || "-"}
          </span>

    },
    {
      field: "requestStatus",
      headerName: "Request Status",
      flex: 1,
      minWidth: 170,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) =>
      <span
        className={`approved-group-status ${params.row.statusKey || normalizeStatus(params.value)}`}>

            {getRequestStatusLabel(params.row.statusKey || params.value)}
          </span>

    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      minWidth: 210,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const canSend =
        params.row.requestStatus === "approved" &&
        !params.row.isSent &&
        params.row.statusKey !== "sent";
        const canDelete =
        params.row.statusKey !== "sent" &&
        params.row.statusKey !== "decline";
        const isBusy = actionRowId === params.row.id;
        const actionLabel = params.row.isSchedule ? "Schedule" : "Send";
        return (
          <div className="approved-group-action-cell">
              <button
              type="button"
              className={`approved-group-action-btn send ${
              canSend && !isBusy ? "enabled" : "disabled"
              }`}
              disabled={!canSend || isBusy}
              onClick={(e) => {
                e.stopPropagation();
                handleSendClick(params.row);
              }}>

                {actionLabel}
              </button>
              {canDelete ?
            <button
              type="button"
              className={`approved-group-action-btn delete ${
              isBusy ? "disabled" : "enabled"
              }`}
              disabled={isBusy}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(params.row);
              }}>

                  Delete
                </button> :

            <span className="approved-group-action-btn-spacer" aria-hidden="true" />}

            </div>);

      }
    }],

    [actionRowId, handleSendClick, handleDeleteClick]
  );

  const gridSx = useMemo(
    () => ({
      border: "none",
      "& .MuiDataGrid-columnHeaders": {
        backgroundColor: "#f8fafc",
        borderBottom: "2px solid #e2e8f0",
        fontSize: "13px",
        fontWeight: "700",
        color: "#1f2937",
        textTransform: "uppercase",
        letterSpacing: "0.5px"
      },
      "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: "700"
      },
      "& .MuiDataGrid-cell": {
        borderBottom: "1px solid #f1f5f9",
        fontSize: "13px",
        padding: "0 12px",
        display: "flex",
        alignItems: "center"
      },
      "& .MuiDataGrid-row": {
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: "#f0f9ff"
        }
      },
      "& .MuiDataGrid-row:nth-of-type(even)": {
        backgroundColor: "#fafafa"
      },
      "& .MuiDataGrid-footerContainer": {
        borderTop: "2px solid #e2e8f0",
        backgroundColor: "#f8fafc",
        minHeight: "56px",
        padding: "0 20px",
        overflow: "hidden"
      },
      "& .MuiTablePagination-root": {
        overflow: "hidden"
      },
      "& .MuiTablePagination-displayedRows": {
        fontSize: "14px",
        fontWeight: "600",
        color: "#374151",
        margin: 0
      },
      "& .MuiTablePagination-selectLabel": {
        display: "none"
      },
      "& .MuiTablePagination-select": {
        display: "none"
      },
      "& .MuiTablePagination-spacer": {
        flex: "none"
      },
      "& .MuiTablePagination-toolbar": {
        minHeight: "56px",
        paddingLeft: "0",
        paddingRight: "0",
        overflow: "hidden"
      },
      "& .MuiTablePagination-actions": {
        marginLeft: "20px",
        overflow: "hidden",
        "& .MuiIconButton-root": {
          backgroundColor: "#3b82f6",
          color: "white",
          borderRadius: "8px",
          padding: "6px",
          marginLeft: "8px",
          transition: "background-color 0.2s ease",
          "&:hover": {
            backgroundColor: "#2563eb"
          },
          "&.Mui-disabled": {
            backgroundColor: "#e5e7eb",
            color: "#9ca3af"
          }
        }
      },
      "& .MuiDataGrid-virtualScroller": {
        "&::-webkit-scrollbar": {
          width: "8px",
          height: "8px"
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f5f9",
          borderRadius: "4px"
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#cbd5e1",
          borderRadius: "4px",
          "&:hover": {
            background: "#94a3b8"
          }
        }
      },
      "& .MuiDataGrid-cell:focus": {
        outline: "none !important"
      }
    }),
    []
  );

  return (
    <div className="approved-group-panel">
      <div className="approved-group-panel__header">
        <h3 className="approved-group-panel__title">{panelTitle}</h3>
        <div className="messages-approval-filter-btn-wrap">
          <button
            type="button"
            className="messages-approval-filter-icon-btn"
            aria-label="Open filters"
            onClick={openFilterDrawer}>

            <FontAwesomeIcon icon={faFilter} />
          </button>
          {filtersActive && <span className="messages-approval-filter-dot" />}
        </div>
      </div>

      <ApprovedGroupMessagesFilterDrawer
        isOpen={filterDrawerOpen}
        onClose={closeFilterDrawer}
        draftFilters={draftFilters}
        onDraftChange={setDraftFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters} />


      <MessagesApprovalSendConfirmModal
        isOpen={sendConfirmOpen}
        loading={sendConfirmLoading}
        isSchedule={Boolean(sendConfirmRow?.isSchedule)}
        onConfirm={handleSendConfirm}
        onCancel={closeSendConfirm} />


      <div className="approved-group-table-wrap">
        <div className={`approved-group-table-panel ${classes.root}`}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={gridPageSize}
            rowsPerPageOptions={[gridPageSize]}
            pagination
            paginationMode="server"
            rowCount={totalCount}
            page={page - 1}
            onPageChange={handlePageChange}
            loading={loading}
            disableSelectionOnClick
            disableColumnMenu
            hideFooterSelectedRowCount
            rowHeight={56}
            headerHeight={48}
            sx={gridSx} />

        </div>
      </div>
    </div>);

};

export default ApprovedGroupMessagesPanel;